import * as Yup from 'yup';
import {
  isValidCnpj,
  isValidCpf,
  maskCep,
  maskCnpj,
  maskCpf,
  maskPhone,
  onlyDigits,
} from './masks';

export type PersonPath = 'customer' | 'vendor' | 'employee' | 'carrier';
export type PersonKind = 'PF' | 'PJ';

export const PERSON_TYPES: {
  path: PersonPath;
  flag: 'is_customer' | 'is_vendor' | 'is_employee' | 'is_carrier';
  label: string;
  plural: string;
  /** Artigo usado nas mensagens ("o cliente", "a transportadora"). */
  article: 'o' | 'a';
}[] = [
  { path: 'customer', flag: 'is_customer', label: 'Cliente', plural: 'Clientes', article: 'o' },
  { path: 'vendor', flag: 'is_vendor', label: 'Fornecedor', plural: 'Fornecedores', article: 'o' },
  { path: 'employee', flag: 'is_employee', label: 'Funcionário', plural: 'Funcionários', article: 'o' },
  { path: 'carrier', flag: 'is_carrier', label: 'Transportadora', plural: 'Transportadoras', article: 'a' },
];

export const getPersonType = (path: string) =>
  PERSON_TYPES.find((type) => type.path === path) ?? PERSON_TYPES[0];

export interface AddressItem {
  /** Chave local para listas; para itens já salvos é o próprio id da API. */
  key: string;
  id?: number;
  address_zip_code: string;
  address_street: string;
  address_number: string;
  address_complement: string;
  address_district: string;
  city: string;
  uf: string;
  address_default?: boolean;
}

export interface ContactItem {
  key: string;
  uuid?: string;
  name_1: string;
  phone: string;
  cell_phone: string;
}

export interface PersonFormValues {
  kind: PersonKind;
  cpf_cnpj: string;
  name: string;
  trading_name: string;
  email: string;
  birthdate: string;
  rg: string;
  foreign_document: string;
  gender: '' | 'm' | 'f';
  state_registration: string;
  free_state_registration: boolean;
  suframa: string;
  municipal_registration: string;
  additional_data: string;
  status: boolean;
  is_customer: boolean;
  is_vendor: boolean;
  is_employee: boolean;
  is_carrier: boolean;
}

let keySeed = 0;
export const newKey = () => `new-${Date.now()}-${keySeed++}`;

const text = (value: unknown) =>
  value === null || value === undefined ? '' : String(value).trim();

/** Registros antigos (PDV) usam datas como "0001-01-01", que não são reais. */
const toDateInput = (value: unknown) => {
  const date = text(value).slice(0, 10);
  return /^\d{4}-\d{2}-\d{2}$/.test(date) && date > '1900-01-01' ? date : '';
};

/** Detecta PF/PJ pelo documento salvo; sem documento, PJ é o padrão de fornecedor/transportadora. */
export const detectKind = (person: any, path: string): PersonKind => {
  const digits = onlyDigits(person?.cpf_cnpj);
  if (digits.length === 14) return 'PJ';
  if (digits.length === 11) return 'PF';
  if (person?.trading_name) return 'PJ';
  return path === 'vendor' || path === 'carrier' ? 'PJ' : 'PF';
};

export function toFormValues(person: any, path: string): PersonFormValues {
  const isNew = !person?.uuid;
  const kind = detectKind(person, path);
  const digits = onlyDigits(person?.cpf_cnpj);
  const flag = (key: string, typePath: PersonPath) =>
    isNew ? path === typePath : Boolean(person?.[key]) || path === typePath;

  return {
    kind,
    // "000.000.000-00" é o placeholder do PDV para "sem documento".
    cpf_cnpj: /^0+$/.test(digits)
      ? ''
      : kind === 'PJ'
        ? maskCnpj(digits)
        : maskCpf(digits),
    name: text(person?.name),
    trading_name: text(person?.trading_name),
    email: text(person?.email),
    birthdate: toDateInput(person?.birthdate),
    rg: text(person?.rg),
    foreign_document: text(person?.foreign_document),
    gender: person?.gender === 'm' || person?.gender === 'f' ? person.gender : '',
    state_registration: text(person?.state_registration),
    free_state_registration: Boolean(Number(person?.free_state_registration)),
    suframa: text(person?.suframa),
    municipal_registration: text(person?.municipal_registration),
    additional_data: text(person?.additional_data),
    status: isNew ? true : Number(person?.status) === 1,
    is_customer: flag('is_customer', 'customer'),
    is_vendor: flag('is_vendor', 'vendor'),
    is_employee: flag('is_employee', 'employee'),
    is_carrier: flag('is_carrier', 'carrier'),
  };
}

export const toAddressItems = (addresses: any[] | undefined): AddressItem[] =>
  (addresses ?? []).map((address) => ({
    key: String(address.id ?? newKey()),
    id: address.id,
    address_zip_code: maskCep(address.address_zip_code),
    address_street: text(address.address_street),
    address_number: text(address.address_number),
    address_complement: text(address.address_complement),
    address_district: text(address.address_district || address.address_neighborhood),
    city: text(address.city),
    uf: text(address.uf).toUpperCase(),
    address_default: Boolean(address.address_default),
  }));

export const toContactItems = (contacts: any[] | undefined): ContactItem[] =>
  (contacts ?? []).map((contact) => ({
    key: String(contact.uuid ?? contact.id ?? newKey()),
    uuid: contact.uuid,
    name_1: text(contact.name_1),
    // O cadastro antigo gravava "---" quando o telefone ficava vazio.
    phone: maskPhone(text(contact.phone).replace(/^-+$/, '')),
    cell_phone: maskPhone(text(contact.cell_phone).replace(/^-+$/, '')),
  }));

export const personSchema = Yup.object({
  kind: Yup.mixed<PersonKind>().oneOf(['PF', 'PJ']).required(),
  cpf_cnpj: Yup.string()
    .default('')
    .when(['kind', 'foreign_document'], ((kind: PersonKind, foreign: string, schema: any) =>
      kind === 'PJ'
        ? schema
            .required('Informe o CNPJ.')
            .test('cnpj', 'CNPJ inválido. Confira os números digitados.', (v: string) =>
              isValidCnpj(v),
            )
        : schema
            .test(
              'cpf-required',
              'Informe o CPF (ou um documento estrangeiro).',
              (v: string) => Boolean(onlyDigits(v)) || Boolean(text(foreign)),
            )
            .test(
              'cpf',
              'CPF inválido. Confira os números digitados.',
              (v: string) => !onlyDigits(v) || isValidCpf(v),
            )) as any,
    ),
  name: Yup.string()
    .trim()
    .max(255, 'Use no máximo 255 caracteres.')
    .when('kind', (kind: PersonKind, schema: any) =>
      schema.required(kind === 'PJ' ? 'Informe a razão social.' : 'Informe o nome.'),
    ),
  email: Yup.string().trim().email('E-mail inválido. Ex.: nome@empresa.com.br'),
  birthdate: Yup.string().test(
    'birthdate',
    'Data de nascimento inválida.',
    (v) => !v || (v > '1900-01-01' && v <= new Date().toISOString().slice(0, 10)),
  ),
  is_customer: Yup.boolean().test(
    'at-least-one-type',
    'Selecione pelo menos um tipo de cadastro.',
    function () {
      const { is_customer, is_vendor, is_employee, is_carrier } = this.parent;
      return is_customer || is_vendor || is_employee || is_carrier;
    },
  ),
});

export const addressSchema = Yup.object({
  address_zip_code: Yup.string().test(
    'cep',
    'CEP deve ter 8 números.',
    (v) => onlyDigits(v).length === 8,
  ),
  address_street: Yup.string().trim().required('Informe o logradouro.'),
  address_number: Yup.string().trim().required('Informe o número (ou "S/N").'),
  address_district: Yup.string().trim().required('Informe o bairro.'),
  city: Yup.string().trim().required('Informe a cidade.'),
  uf: Yup.string()
    .trim()
    .required('Informe a UF.')
    .matches(/^[A-Za-z]{2}$/, 'UF deve ter 2 letras.'),
});

export const contactSchema = Yup.object({
  name_1: Yup.string().trim().required('Informe o nome do contato.'),
  phone: Yup.string()
    .test('phone', 'Telefone incompleto.', (v) => {
      const digits = onlyDigits(v);
      return !digits || digits.length >= 10;
    })
    .test('one-phone', 'Informe um telefone ou celular.', function (v) {
      return Boolean(onlyDigits(v)) || Boolean(onlyDigits(this.parent.cell_phone));
    }),
  cell_phone: Yup.string().test('cell', 'Celular incompleto.', (v) => {
    const digits = onlyDigits(v);
    return !digits || digits.length >= 10;
  }),
});

/** Remove vazios: a API converte "" em null e recusa null em vários campos. */
const compact = (obj: Record<string, any>) =>
  Object.fromEntries(
    Object.entries(obj).filter(([, value]) => value !== '' && value !== undefined),
  );

export const toAddressPayload = (address: AddressItem) =>
  compact({
    id: address.id,
    address_zip_code: onlyDigits(address.address_zip_code),
    address_street: address.address_street.trim(),
    address_number: address.address_number.trim(),
    address_complement: address.address_complement.trim(),
    address_district: address.address_district.trim(),
    address_neighborhood: address.address_district.trim(),
    city: address.city.trim(),
    uf: address.uf.trim().toUpperCase(),
    address_default: Boolean(address.address_default),
  });

export const toContactPayload = (contact: ContactItem) =>
  compact({
    uuid: contact.uuid,
    name_1: contact.name_1.trim(),
    phone: onlyDigits(contact.phone),
    cell_phone: onlyDigits(contact.cell_phone),
  });

const PF_ONLY = ['birthdate', 'rg', 'foreign_document', 'gender'] as const;
const PJ_ONLY = ['trading_name', 'state_registration', 'suframa', 'municipal_registration'] as const;

/**
 * Monta o corpo do POST/PUT de /people.
 * Na edição, campos limpos vão como null para que a API apague o valor
 * (exceto additional_data, que a API não aceita nulo).
 */
export function buildPersonPayload(
  values: PersonFormValues,
  options: { storeId?: number; uuid?: string },
) {
  const isEdit = Boolean(options.uuid);
  const isPJ = values.kind === 'PJ';
  const empty = isEdit ? null : undefined;
  const field = (value: string) => (value.trim() === '' ? empty : value.trim());

  const payload: Record<string, any> = {
    ...(isEdit ? { uuid: options.uuid } : { store_id: options.storeId }),
    cpf_cnpj: onlyDigits(values.cpf_cnpj) || undefined,
    name: values.name.trim(),
    email: field(values.email),
    additional_data: values.additional_data.trim() || undefined,
    status: values.status ? 1 : 0,
    is_customer: values.is_customer,
    is_vendor: values.is_vendor,
    is_employee: values.is_employee,
    is_carrier: values.is_carrier,
  };

  if (isPJ) {
    payload.trading_name = field(values.trading_name);
    payload.suframa = field(values.suframa);
    payload.municipal_registration = field(values.municipal_registration);
    payload.free_state_registration = values.free_state_registration;
    payload.state_registration = values.free_state_registration
      ? undefined
      : field(values.state_registration);
    PF_ONLY.forEach((key) => (payload[key] = empty));
  } else {
    payload.birthdate = field(values.birthdate);
    payload.rg = field(values.rg);
    payload.foreign_document = field(values.foreign_document);
    payload.gender = values.gender || empty;
    PJ_ONLY.forEach((key) => (payload[key] = empty));
  }

  return Object.fromEntries(
    Object.entries(payload).filter(([, value]) => value !== undefined),
  );
}

export const describeTypes = (person: any) =>
  PERSON_TYPES.filter((type) => Boolean(person?.[type.flag])).map((type) => type.label);
